import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import useFlexibleData from "../../../../../hooks/useFlexibleData";

/** 금액·비고 모두 비어 있으면 고지서 본표에서 행 숨김 */
function billItemRowIsEmpty(item, remarkText) {
  const amountEmpty =
    item?.amount == null ||
    item.amount === "" ||
    (typeof item.amount === "number" && !Number.isFinite(item.amount));
  const remarkEmpty = String(remarkText ?? "").trim() === "";
  return amountEmpty && remarkEmpty;
}

const billSupportsFieldSizing =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("field-sizing", "content");

/** 고지서용 textarea: 내용 높이에 맞춤. resize 핸들 없음( CSS + webkit ) */
function BillAutosizeTextarea({ value, onChange, ariaLabel, className }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (billSupportsFieldSizing) return;
    const el = ref.current;
    if (!el) return;
    el.style.minHeight = "0";
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      className={[className, "bill-autosize"].filter(Boolean).join(" ")}
      value={value}
      spellCheck={false}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    />
  );
}

function BillCard({
  studentData,
  currentSession,
  accountMessageData,
  logoImg,
  logoImgStyle,
  schoolYearLabel,
}) {
  const formatAmount = (val) => {
    if (val === null || val === undefined || val === "") return "0";
    return Number(val).toLocaleString();
  };

  const accountLineInitial =
    accountMessageData?.[studentData?.hierarchyOne] || "";

  const [remarks, setRemarks] = useState([]);
  const [accountLine, setAccountLine] = useState("");
  const [periodMessage, setPeriodMessage] = useState("");
  const [issueDate, setIssueDate] = useState("");

  useEffect(() => {
    const items = studentData?.items || [];
    setRemarks(items.map((i) => i.remark || ""));
    setAccountLine(accountLineInitial);
    const pm = String(studentData?.payment_method || "").trim();
    const cms = currentSession?.cms_message ?? currentSession?.cmsMessage ?? "";
    const unreg =
      currentSession?.unreg_message ?? currentSession?.unregMessage ?? "";
    if (pm === "미등록") {
      setPeriodMessage(unreg);
    } else if (pm === "CMS") {
      setPeriodMessage(cms);
    } else {
      setPeriodMessage("");
    }
    setIssueDate(currentSession?.issue_date ?? currentSession?.issueDate ?? "");
  }, [studentData, currentSession, accountLineInitial]);

  const yearText =
    schoolYearLabel != null && schoolYearLabel !== ""
      ? `${schoolYearLabel}학년도`
      : "";
  const classAndRoom = [studentData?.hierarchyTwo, studentData?.hierarchyThree]
    .filter(Boolean)
    .join(" ");

  const setRemarkAt = (idx, v) => {
    setRemarks((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
  };

  const visibleItemEntries = useMemo(() => {
    const items = studentData?.items || [];
    return items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item, idx }) => {
        const remarkText = remarks[idx] ?? item.remark ?? "";
        return !billItemRowIsEmpty(item, remarkText);
      });
  }, [studentData?.items, remarks]);

  const paymentMethod = String(studentData?.payment_method || "").trim();

  return (
    <div className="bill-page-fixed">
      <div className="bill-title-wrap">
        <p className="bill-head mb-0 ms-3">{yearText}</p>
        <p className="bill-title mb-0">{currentSession?.session_name} 고지서</p>
      </div>

      <div className="mini-info-wrap">
        <table className="mini-info">
          <tbody>
            <tr>
              <th className="text-center">과정</th>
              <td className="text-center">{studentData?.hierarchyOne || ""}</td>
            </tr>
            <tr>
              <th className="text-center">학생명</th>
              <td className="text-center">
                {classAndRoom ? `${classAndRoom} ` : ""}
                {studentData?.fullname || ""}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <table className="bill-table">
        <thead>
          <tr>
            <th className="col-desc" style={{ width: "120px" }}>
              내역
            </th>
            <th
              className="col-amt-title"
              style={{ width: "120px" }}
              colSpan={2}
            >
              금액
            </th>
            <th className="col-remark">비고</th>
          </tr>
        </thead>
        <tbody>
          {visibleItemEntries.map(({ item, idx }) => (
            <tr key={`${item.name}-${idx}`}>
              <th className="col-desc" scope="row">
                {item.name || ""}
              </th>
              <td className="won" style={{ borderRight: "none" }}>
                ₩
              </td>
              <td className="amount" style={{ borderLeft: "none" }}>
                {formatAmount(item.amount)}
              </td>
              <td className="remark">
                <BillAutosizeTextarea
                  className="bill-edit bill-edit-remark"
                  value={remarks[idx] ?? ""}
                  onChange={(v) => setRemarkAt(idx, v)}
                  ariaLabel="비고"
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bill-sum-row">
            <th className="col-desc" scope="row">
              합계
            </th>
            <th className="won" style={{ borderRight: "none" }}>
              ₩
            </th>
            <th className="amount" style={{ borderLeft: "none" }}>
              {formatAmount(studentData?.total)}
            </th>
            <th className="col-remark-sum" />
          </tr>
        </tfoot>
      </table>

      {paymentMethod === "미등록" ? (
        <BillAutosizeTextarea
          className="bill-edit bill-edit-account"
          value={accountLine}
          onChange={setAccountLine}
          ariaLabel="계좌 안내"
        />
      ) : null}

      {(paymentMethod === "CMS" || paymentMethod === "미등록") && (
        <BillAutosizeTextarea
          className="bill-edit bill-edit-period"
          value={periodMessage}
          onChange={setPeriodMessage}
          ariaLabel="CMS 또는 미등록 안내"
        />
      )}
      <input
        type="text"
        className="bill-edit bill-edit-date"
        value={issueDate}
        onChange={(e) => setIssueDate(e.target.value)}
        aria-label="발행일"
      />
      {logoImg ? (
        <div className="logo-wrap">
          <img src={logoImg} alt="logo" style={logoImgStyle || undefined} />
        </div>
      ) : null}
    </div>
  );
}

export default function BillingPrintPage({
  schoolId,
  schoolYear,
  selectedSessionId,
  selectedPrintUsername,
  selectedCourseName,
  selectedClassId,
  currentSession,
  currentSessionItems,
  resolvedStudentList,
  getBillingRecord,
  handleToastCenterTop,
  canRead,
  canCreate,
  canUpdate,
}) {
  const { t } = useTranslation();
  const canSaveAccountMessage = Boolean(canCreate || canUpdate);
  const [accountText, setAccountText] = useState("");
  const singlePrintRef = useRef(null);
  const bulkPrintRef = useRef(null);

  const {
    flexibleDataQuery: { data: billingFlexibleData },
    createOrUpdateFlexibleData,
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "billingManagement",
  });

  const accountMessageData = useMemo(
    () => billingFlexibleData?.[0]?.data_value?.accountMessage || {},
    [billingFlexibleData],
  );
  const logoImg = accountMessageData?.logoImg || "";
  const logoImgStyle = accountMessageData?.logoImgStyle || null;

  const makeStudentBill = useCallback(
    (student) => {
      const items = [];
      let total = 0;
      currentSessionItems.forEach((si) => {
        const record = getBillingRecord(
          selectedSessionId,
          student.username,
          si.id,
        );
        const amount = record?.amount ?? null;
        items.push({
          name: si.item_name,
          amount,
          remark: record?.bill_remark || "",
        });
        total += Number(amount) || 0;
      });
      return { ...student, items, total };
    },
    [currentSessionItems, getBillingRecord, selectedSessionId],
  );

  const selectedStudentData = useMemo(() => {
    if (!selectedPrintUsername || !selectedSessionId) return null;
    const student = resolvedStudentList.find(
      (s) => s.username === selectedPrintUsername,
    );
    if (!student) return null;
    return makeStudentBill(student);
  }, [
    selectedPrintUsername,
    selectedSessionId,
    resolvedStudentList,
    makeStudentBill,
  ]);

  const bulkStudentsData = useMemo(
    () => resolvedStudentList.map((student) => makeStudentBill(student)),
    [resolvedStudentList, makeStudentBill],
  );

  const currentCourse =
    selectedCourseName || selectedStudentData?.hierarchyOne || "";
  const currentCourseAccountMessage = currentCourse
    ? accountMessageData?.[currentCourse] || ""
    : "";

  useEffect(() => {
    setAccountText(currentCourseAccountMessage);
  }, [currentCourseAccountMessage]);

  const pageStyle = `
      @media print {
        body { background-color: transparent !important; }
        @page { size: A4 portrait; margin: 10mm; }
        .no-print-page { display: none !important; }
        .print-page { display: block; width: 210mm; margin: auto; }
        .bill-page-fixed { page-break-after: always; }
        .bulk-print-hidden { position: static !important; left: auto !important; }
        .bill-edit,
        .bill-edit:focus,
        .bill-edit:focus-visible {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
      }
    `;

  const triggerSinglePrint = useReactToPrint({
    content: () => singlePrintRef.current,
    documentTitle: `${schoolYear || ""}-${currentSession?.session_name || ""}-${selectedStudentData?.fullname || ""}`,
    removeAfterPrint: true,
    pageStyle,
  });

  const triggerBulkPrint = useReactToPrint({
    content: () => bulkPrintRef.current,
    documentTitle: `${schoolYear || ""}-${currentSession?.session_name || ""}-${t("billingManagement.printPage.documentTitleBulkSuffix") || "반일괄"}`,
    removeAfterPrint: true,
    pageStyle,
  });

  const handlePrintOne = () => {
    if (!canRead) {
      handleToastCenterTop(
        t("billingManagement.messages.printNoPermission") ||
          "인쇄 권한이 없습니다.",
      );
      return;
    }
    if (!selectedStudentData) {
      handleToastCenterTop(
        t("billingManagement.messages.selectStudentFirst") ||
          "학생을 선택해주세요.",
      );
      return;
    }
    triggerSinglePrint();
  };

  const handleBulkPrint = () => {
    if (!canRead) {
      handleToastCenterTop(
        t("billingManagement.messages.printNoPermission") ||
          "인쇄 권한이 없습니다.",
      );
      return;
    }
    if (!selectedSessionId) {
      handleToastCenterTop(
        t("billingManagement.messages.selectPeriodFirst") ||
          "기간을 선택해주세요.",
      );
      return;
    }
    if (!selectedClassId || selectedClassId === "0") {
      handleToastCenterTop(
        t("billingManagement.messages.selectClassForPrint") ||
          "반을 선택 후에 인쇄 가능합니다.",
      );
      return;
    }
    if (bulkStudentsData.length === 0) {
      handleToastCenterTop(
        t("billingManagement.messages.noStudentsToPrint") ||
          "인쇄할 학생이 없습니다. 반을 선택해주세요.",
      );
      return;
    }
    triggerBulkPrint();
  };

  const handleSaveAccountMessage = () => {
    if (!canSaveAccountMessage) {
      handleToastCenterTop(
        t("billingManagement.permissions.noSave") || "저장 권한이 없습니다.",
      );
      return;
    }
    if (!currentCourse) {
      handleToastCenterTop(
        t("billingManagement.messages.selectCourseForAccount") ||
          "과정을 선택해주세요.",
      );
      return;
    }
    const nextAccountMessage = {
      ...(accountMessageData || {}),
      [currentCourse]: accountText || "",
      logoImg: accountMessageData?.logoImg || "",
    };

    const payload = {
      ...(billingFlexibleData?.[0]?.id
        ? { id: billingFlexibleData[0].id }
        : {}),
      schoolid: schoolId,
      main_category: "billingManagement",
      data_value: { accountMessage: nextAccountMessage },
    };

    createOrUpdateFlexibleData.mutate(payload, {
      onSuccess: () =>
        handleToastCenterTop(
          t("billingManagement.messages.accountSaved") ||
            "계좌 정보 저장완료",
        ),
      onError: (e) =>
        handleToastCenterTop(
          t("billingManagement.messages.saveFailedPrefix", {
            detail: e?.message || "",
          }) || `저장 실패: ${e?.message || ""}`,
        ),
    });
  };

  if (!selectedSessionId) {
    return (
      <div className="alert alert-info mt-3">
        {t("billingManagement.messages.selectPeriodFirst") ||
          "기간을 선택해주세요."}
      </div>
    );
  }

  return (
    <div>
      <style>{`
          .bill-preview-wrap {
            width: 100%;
            min-height: calc(100vh - 250px);
            background: #dcdde1;
            border: 1px solid #cfd2d7;
            padding: 20px;
            box-sizing: border-box;
          }
          .bill-page-fixed {
            width: 10.5cm;
            min-height: 12cm;
            border: 2px solid #222;
            background: #fff;
            margin: 0;
            padding: 6mm 5mm;
            box-sizing: border-box;
            font-size: 12px;
            font-weight: 700;
          }
          .bill-head { font-size: 20px; margin-bottom: 2mm; text-align: left; }
          .bill-title-wrap { background: #d9d9d9; margin-bottom: 3mm; }
          .bill-title { font-size: 30px; text-align: center; padding: 0; }
          .mini-info-wrap {
            width: 60%;
            margin: 0 0 3mm 0;
          }
          .mini-info {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 0;
            font-size: 12px;
          }
          .mini-info th, .mini-info td {
            border: 2px solid #222;
            padding: 3px;
            text-align: left;
          }
          .mini-info th { width: 28%; background: #d9d9d9; }
          .bill-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 3mm;
            font-size: 12px;
          }
          .bill-table thead th,
          .bill-table tbody th,
          .bill-table tbody td,
          .bill-table tfoot th {
            border: 2px solid #222;
            padding: 3px;
          }
          .bill-table thead th {
            background: #d9d9d9;
            text-align: center;
          }
          .bill-table tbody th.col-desc {
            background: #d9d9d9;
            text-align: center;
            font-weight: 700;
          }
          .bill-table .won {
            width: 8mm;
            text-align: center;
            background: #fff;
          }
          .bill-table .amount {
            width: 21mm;
            text-align: right;
            background: #fff;
          }
          .bill-table .remark {
            font-size: 90%;
            text-align: center;
            vertical-align: top;
            background: #fff;
          }
          .bill-sum-row th {
            background: #d9d9d9 !important;
            text-align: center;
          }
          .bill-sum-row .amount {
            text-align: right;
            background: #d9d9d9 !important;
          }
          .bill-sum-row .won {
            background: #d9d9d9 !important;
          }
          .bill-sum-row .col-remark-sum {
            background: #d9d9d9 !important;
          }
          .bill-edit {
            font: 12px;
            font-weight: inherit;
            box-sizing: border-box;
            width: 100%;
            margin: 0;
            padding: 0;
            border: none;
            border-radius: 0;
            outline: none;
            background: transparent;
            box-shadow: none;
            -webkit-appearance: none;
            appearance: none;
          }
          .bill-edit:focus,
          .bill-edit:focus-visible {
            outline: 2px solid rgba(13, 110, 253, 0.45);
            outline-offset: 1px;
          }
          textarea.bill-edit {
            resize: none !important;
          }
          textarea.bill-edit::-webkit-resizer {
            display: none;
          }
          .bill-autosize {
            field-sizing: content;
            min-height: 1lh;
            max-height: none;
            overflow: hidden;
          }
          .bill-edit-remark {
            text-align: center;
            white-space: pre-wrap;
            word-break: break-word;
            line-height: 1.25;
          }
          .bill-edit-account {
            display: block;
            margin-top: 4mm;
            color: #0d6efd;
            text-align: center;
            font-size: 14px;
            font-weight: 700;
            white-space: pre-wrap;
            line-height: 1.25;
          }
          .bill-edit-period {
            display: block;
            margin-top: 3mm;
            color: #222;
            text-align: center;
            font-size: 12px;
            font-weight: 700;
            white-space: pre-wrap;
            line-height: 1.25;
          }
          .bill-edit-date {
            display: block;
            margin-top: 3mm;
            text-align: center;
            font-size: 12px;
            font-weight: 700;
            color: #222;
          }
          @media print {
            .bill-edit,
            .bill-edit:focus,
            .bill-edit:focus-visible {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
              resize: none !important;
            }
            .bill-autosize,
            .bill-edit-remark {
              overflow: visible !important;
            }
          }
          .logo-wrap { margin-top: 4mm; text-align: center; }
          .logo-wrap img { width: 168px; height: 50px; object-fit: contain; }
          .bulk-print-hidden {
            position: absolute;
            left: -99999px;
            top: 0;
            width: 210mm;
            background: #fff;
          }
        `}</style>

      <div className="d-flex justify-content-end gap-2 mb-2 no-print-page">
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={handlePrintOne}
          disabled={!canRead || !selectedStudentData}
        >
          {t("common.labels.print") || "인쇄"}
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleBulkPrint}
          disabled={!canRead}
        >
          {t("common.labels.bulkPrint") || "반일괄인쇄"}
        </button>
      </div>

      <div className="row no-print-page">
        <div className="col-lg-7">
          <div
            ref={singlePrintRef}
            className="d-flex justify-content-center print-page"
          >
            {selectedStudentData ? (
              <div style={{ width: "205mm" }}>
                <BillCard
                  key={selectedStudentData.username}
                  studentData={selectedStudentData}
                  currentSession={currentSession}
                  accountMessageData={accountMessageData}
                  logoImg={logoImg}
                  logoImgStyle={logoImgStyle}
                  schoolYearLabel={schoolYear}
                />
              </div>
            ) : (
              <div className="text-muted">
                {t("billingManagement.printPage.selectStudentHint") ||
                  "학생을 선택하면 고지서 양식이 표시됩니다."}
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-5">
          <h6 className="mb-1">
            {t("billingManagement.labels.accountInfoTitle", {
              course:
                currentCourse ||
                t("billingManagement.labels.selectCourse") ||
                "과정 선택",
            }) || `${currentCourse || "과정 선택"} 계좌 정보`}
          </h6>
          <textarea
            className="form-control form-control-sm"
            rows={4}
            value={accountText}
            onChange={(e) => setAccountText(e.target.value)}
          />
          <div className="small text-muted mt-1">
            {t("billingManagement.labels.accountFooterHint") ||
              "미등록 학생의 고지서에 표시됩니다."}
          </div>
          <div className="text-end mt-1">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={handleSaveAccountMessage}
              disabled={!canSaveAccountMessage}
            >
              {t("common.labels.save") || "저장"}
            </button>
          </div>
        </div>
      </div>

      <div ref={bulkPrintRef} className="bulk-print-hidden print-page">
        <div style={{ width: "205mm" }}>
          {bulkStudentsData.map((student, idx) => (
            <div
              key={`${student.username}-${idx}`}
              className={idx > 0 ? "page-break" : ""}
            >
              <BillCard
                studentData={student}
                currentSession={currentSession}
                accountMessageData={accountMessageData}
                logoImg={logoImg}
                logoImgStyle={logoImgStyle}
                schoolYearLabel={schoolYear}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
